import { generatePath } from 'react-router-dom';
import Immutable from 'immutable';
import { stringify as stringifyParams } from 'hub-http/helpers/params';
import * as ThemeQueryField from '../constants/ThemeQueryField';
import AssetBusinessTypeUrls from '../constants/urls/AssetBusinessType';
import AssetPageFeatureUrls from '../constants/urls/AssetPageFeature';
import AssetPageTypeUrls from '../constants/urls/AssetPageType';
import ThemeQuery, { DEFAULT_THEME_SORT_VALUE, DEFAULT_THEME_QUERY_LIMIT, THEME_QUERY_FILTER_ORDER } from '../records/ThemeQuery';
import { getOffsetFromPageNumber, getPageNumberFromOffset, getPaginatedPathname } from './pagination';
import * as ThemeSort from '../constants/ThemeSort';
const MAX_PATH_PARAMS = 2;
const QUERY_STRING_DISALLOW_LIST = [ThemeQueryField.OFFERING_IDS, ThemeQueryField.LIMIT, ThemeQueryField.OFFSET];

function hasFieldValue(themeQueryObj, field) {
  const value = themeQueryObj[field];
  return Array.isArray(value) ? !!value.length : value != null && value !== '';
}

export function getComputedThemeQuery(parsedParams, location, defaultSortFields, defaultLimit) {
  const {
    businessType,
    pageFeature,
    pageNumber,
    pageType
  } = parsedParams;
  const {
    search
  } = location;
  const offset = getOffsetFromPageNumber(pageNumber, DEFAULT_THEME_QUERY_LIMIT);
  return ThemeQuery.fromLocation({
    search
  }).mergeWith((oldVal, newVal) => {
    return newVal != null ? newVal : oldVal;
  }, {
    [ThemeQueryField.LIMIT]: defaultLimit || DEFAULT_THEME_QUERY_LIMIT,
    [ThemeQueryField.OFFSET]: offset,
    [ThemeQueryField.SORT_FIELDS]: defaultSortFields ? Immutable.Set(defaultSortFields) : undefined,
    [ThemeQueryField.BUSINESS_TYPES]: businessType ? Immutable.Set([businessType]) : undefined,
    [ThemeQueryField.PAGE_TYPES]: pageType ? Immutable.Set([pageType]) : undefined,
    [ThemeQueryField.PAGE_FEATURES]: pageFeature ? Immutable.Set([pageFeature]) : undefined
  });
}
export function getLocationFromThemeQuery(themeQuery, currentLocation, isCollection = false) {
  const {
    pathname: currentPathname
  } = currentLocation;
  let pathPattern;
  let pathParams = {};

  if (isCollection) {
    pathPattern = currentPathname;
  } else {
    ({
      pathPattern,
      pathParams
    } = getPathnameFromThemeQuery(themeQuery));
  }

  const pathname = generatePath(pathPattern, pathParams);
  const pageNumber = getPageNumberFromOffset(themeQuery.get(ThemeQueryField.OFFSET), DEFAULT_THEME_QUERY_LIMIT);
  const queryString = toQueryString(themeQuery, pathParams);
  return {
    pathname: getPaginatedPathname(pathname, pageNumber),
    search: queryString ? `?${queryString}` : ''
  };
}
export function getPathnameFromThemeQuery(themeQuery) {
  let pathParams = {};
  let pathPattern;
  const pathParts = [];
  const queryObj = themeQuery.toJS();
  const queryFieldParamKeyTuples = [[ThemeQueryField.BUSINESS_TYPES, 'businessType'], [ThemeQueryField.PAGE_TYPES, 'pageType'], [ThemeQueryField.PAGE_FEATURES, 'pageFeature']];

  for (const [queryField, paramKey] of queryFieldParamKeyTuples) {
    const values = queryObj[queryField];

    if (values && values.length === 1) {
      const paramValue = getThemeQueryFieldUrl(queryField, values[0]);

      if (paramValue) {
        pathParams[paramKey] = paramValue;
        pathParts.push(paramKey);
      }
    }
  }

  if (pathParts.length && pathParts.length <= MAX_PATH_PARAMS) {
    pathPattern = `/${pathParts.map(part => `:${part}`).join('/')}`;
  } else {
    pathPattern = '/all';
    pathParams = {};
  }

  return {
    pathPattern,
    pathParams
  };
}
export function getThemeQueryFieldUrl(themeQueryField, value) {
  const urls = themeQueryField === 'businessTypes' ? AssetBusinessTypeUrls : themeQueryField === 'pageTypes' ? AssetPageTypeUrls : themeQueryField === 'pageFeatures' ? AssetPageFeatureUrls : {};
  return value in urls ? urls[value] : null;
}
export function toQueryString(themeQuery, pathParams) {
  const queryObj = themeQuery.toJS();
  const modifiedQueryObj = Object.keys(queryObj).reduce((final, key) => {
    const value = queryObj[key];

    if (QUERY_STRING_DISALLOW_LIST.includes(key) || !hasFieldValue(queryObj, key)) {
      return final;
    }

    if (pathParams && (key === ThemeQueryField.BUSINESS_TYPES && pathParams.businessType || key === ThemeQueryField.PAGE_TYPES && pathParams.pageType || key === ThemeQueryField.PAGE_FEATURES && pathParams.pageFeature)) {
      return final;
    }

    return Object.assign({}, final, {
      [key]: value
    });
  }, {});

  if (ThemeQueryField.PRICE_RANGE in modifiedQueryObj) {
    if (!(ThemeQueryField.PLAN_TYPE in modifiedQueryObj)) {
      const {
        minCents,
        maxCents
      } = modifiedQueryObj[ThemeQueryField.PRICE_RANGE];
      modifiedQueryObj.priceRangeMin = minCents;
      modifiedQueryObj.priceRangeMax = maxCents;
    }

    delete modifiedQueryObj[ThemeQueryField.PRICE_RANGE];
  }

  const prefixedQueryObj = Object.keys(modifiedQueryObj).reduce((final, key) => Object.assign({}, final, {
    [`eco_${key}`]: modifiedQueryObj[key]
  }), {});
  return stringifyParams(prefixedQueryObj);
}
export function toFilterTags(themeQuery) {
  const queryObj = themeQuery.toJS();
  return THEME_QUERY_FILTER_ORDER.reduce((tags, field) => {
    const value = queryObj[field];
    if (!value) return tags;
    const fieldTags = Array.isArray(value) ? value.map(val => ({
      name: field,
      value: val
    })) : {
      name: field,
      value
    };
    return tags.concat(fieldTags);
  }, []);
}
export function toSearchQuery(themeQuery) {
  const query = themeQuery.toJS();
  const isCustomRange = query.priceRange !== null;
  return Object.keys(query).reduce((finalQuery, key_) => {
    const key = key_;
    let value = query[key];

    if (key === ThemeQueryField.SORT_FIELDS && !value.length) {
      value = [DEFAULT_THEME_SORT_VALUE];
    }

    if (Array.isArray(value)) {
      if (value.length) {
        switch (key) {
          case ThemeQueryField.SORT_FIELDS:
            {
              const primarySortField = value[0];

              if (primarySortField === ThemeSort.RATING_OVERALL_DESC) {
                value = [ThemeSort.RATING_OVERALL_DESC, ThemeSort.REVIEW_COUNT_DESC];
                finalQuery[ThemeQueryField.MIN_REVIEW_COUNT] = 3;
              }

              break;
            }

          case ThemeQueryField.OFFERING_IDS:
            {
              break;
            }

          default:
            {
              value = {
                clause: 'OR',
                values: value
              };
              break;
            }
        }
      } else {
        value = undefined;
      }
    }

    if (key === ThemeQueryField.PLAN_TYPE && isCustomRange) {
      value = undefined;
    }

    if (value && key === ThemeQueryField.MIN_OVERALL_RATING) {
      finalQuery[ThemeQueryField.MIN_REVIEW_COUNT] = 3;
    }

    if (value != null) {
      finalQuery[key] = value;
    }

    return finalQuery;
  }, {});
}